import { useState } from "react";
import { API } from "@/api";
import { FileText, X, ClipboardList, Tags } from "lucide-react";
import { kenmerkMeta } from "@/lib/kenmerkIcons";

// Building "aandachtskaart": opens an in-app popup showing the info text,
// building characteristics (kenmerken) and one or more documents/images.
export function BuildingInfo({ building, token }) {
  const [open, setOpen] = useState(false);
  const [kenOpen, setKenOpen] = useState(false);
  const [viewer, setViewer] = useState(null);
  if (!building) return null;
  const docs = building.documents || [];
  const kenmerken = building.kenmerken || [];
  if (!building.info_text && docs.length === 0 && kenmerken.length === 0) return null;
  const tok = token || localStorage.getItem("token");
  const fileUrl = (d) => `${API}/buildings/${building.id}/documents/${d.id}/file?auth=${tok}`;
  const isImg = (d) => (d.type || "").startsWith("image") || /\.(png|jpe?g|webp|gif|heic|heif)$/i.test(d.name || "");

  const close = () => { setOpen(false); setViewer(null); };

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="building-info">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-bold text-amber-900"><FileText style={{ width: 16, height: 16 }} /> Aandachtskaart · {building.name}</div>
        <div className="flex items-center gap-2">
          {kenmerken.length > 0 && (
            <button data-testid="open-building-kenmerken" onClick={() => setKenOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100">
              <Tags className="h-3.5 w-3.5" /> Kenmerken ({kenmerken.length})
            </button>
          )}
          {(building.info_text || docs.length > 0) && (
            <button data-testid="open-building-card" onClick={() => setOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-500">
              <ClipboardList className="h-3.5 w-3.5" /> Openen{docs.length > 0 ? ` (${docs.length})` : ""}
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[2000] flex flex-col bg-slate-900/80 p-4" onClick={close}>
          <div className="mx-auto flex max-h-full w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><FileText className="h-4 w-4 text-amber-600" /> Aandachtskaart · {building.name}</div>
              <button data-testid="close-building-card" onClick={close} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4 overflow-y-auto p-5">
              {building.info_text && <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{building.info_text}</p>}
              {docs.length > 0 && (
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Documenten &amp; afbeeldingen ({docs.length})</div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {docs.map((d) => (
                      <button key={d.id} data-testid={`building-doc-${d.id}`} onClick={() => setViewer(d)}
                        className="group overflow-hidden rounded-xl border border-slate-200 bg-slate-50 text-left transition-colors hover:border-amber-400">
                        {isImg(d)
                          ? <img src={fileUrl(d)} alt={d.name} className="h-28 w-full object-cover" />
                          : <div className="flex h-28 w-full items-center justify-center bg-slate-100"><FileText className="h-8 w-8 text-slate-400" /></div>}
                        <div className="truncate px-2 py-1.5 text-[11px] font-medium text-slate-600">{d.name}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {viewer && (
            <div className="fixed inset-0 z-[2100] flex flex-col bg-slate-900/90 p-4" onClick={(e) => { e.stopPropagation(); setViewer(null); }}>
              <div className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                  <div className="truncate text-sm font-bold text-slate-900">{viewer.name}</div>
                  <button data-testid="close-doc-viewer" onClick={() => setViewer(null)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
                </div>
                {isImg(viewer)
                  ? <div className="flex flex-1 items-center justify-center overflow-auto bg-slate-50 p-3"><img src={fileUrl(viewer)} alt={viewer.name} className="max-h-full max-w-full object-contain" /></div>
                  : <iframe title={viewer.name} src={fileUrl(viewer)} className="h-full w-full flex-1" />}
              </div>
            </div>
          )}
        </div>
      )}

      {kenOpen && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/70 p-4" onClick={() => setKenOpen(false)}>
          <div data-testid="building-kenmerken-popup" className="w-full max-w-sm rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-900"><Tags className="h-4 w-4 text-blue-600" /> Gebouwkenmerken · {building.name}</div>
              <button data-testid="close-building-kenmerken" onClick={() => setKenOpen(false)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex flex-wrap gap-2 p-5">
              {kenmerken.map((k, i) => { const M = kenmerkMeta(k); return (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold" style={{ color: M.color }}>
                  <M.Icon className="h-4 w-4" /> {k}
                </span>
              ); })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
